import { useTranslation } from 'react-i18next'
import { APP_CONFIG } from '../data/constants.jsx'

/**
 * The Google Play mark, drawn inline.
 *
 * Not fetched from a CDN: the badge sits in the hero and the footer of every
 * page, and a remote image that loads late makes the layout jump on the slow
 * connections most members are on.
 */
const PlayStoreIcon = ({ className = '' }) => (
  <svg viewBox="0 0 512 512" className={className} aria-hidden="true" focusable="false">
    <path fill="#00C853" d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1z" />
    <path fill="#00A0FF" d="M47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0z" />
    <path fill="#FFCE00" d="M465.2 225.4l-58.9-34.1-65.7 65.3 65.7 65.3 60.1-34.1c18-14.3 18-46.1-1.2-62.4z" />
    <path fill="#FF3A44" d="M104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
  </svg>
)

/**
 * "Get it on Google Play" — the Android app download link.
 *
 * Two skins, because it appears on both a light page and the dark footer:
 *   dark  — near-black pill, the shape people recognise, for light sections
 *   light — inverted, for the footer's dark ground
 *
 * Kept to Google's own wording and proportions rather than restyled to the
 * app's green: the badge is recognised by its shape and colour, and a member
 * scanning the page for "where do I download this" finds it faster when it
 * looks like every other Play badge they have ever seen.
 */
const GetAppButton = ({ variant = 'dark', className = '' }) => {
  const { t, i18n } = useTranslation()

  // Bangla has no letter case, and letter-spacing pulls its conjuncts apart —
  // "ডাউনলোড করুন" set in the Latin badge style reads as broken type. The
  // small-caps treatment is applied only to the script it was designed for.
  const bn = i18n.language === 'bn'
  const kicker = bn
    ? 'text-xs font-medium'
    : 'text-[10px] font-medium uppercase tracking-[0.12em]'

  const skins = {
    dark: `bg-[#101418] text-white border-white/10
           hover:bg-[#1c2228] focus-visible:ring-offset-background`,
    light: `bg-background text-foreground border-black/10
            hover:bg-muted focus-visible:ring-offset-foreground`,
  }

  return (
    <a
      href={APP_CONFIG.playStoreUrl}
      target="_blank"
      rel="noopener noreferrer"
      // min-h-[56px] keeps it a comfortable tap target; the badge is most often
      // pressed on the very phones it installs onto.
      className={`inline-flex items-center gap-3 rounded-xl border px-5 py-2.5 min-h-[56px]
                  transition-all duration-200 hover:-translate-y-0.5 hover:shadow-strong
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
                  focus-visible:ring-offset-2
                  ${skins[variant]} ${className}`}
    >
      <PlayStoreIcon className="w-7 h-7 shrink-0" />
      <span className="text-left leading-tight">
        <span className={`block opacity-70 ${kicker}`}>
          {t('app.getItOn')}
        </span>
        {/* A brand name — never translated, never transliterated. */}
        <span className="block text-lg font-semibold leading-snug">Google Play</span>
      </span>
    </a>
  )
}

export default GetAppButton
